import React, { useState, useEffect } from 'react';
import { Modal } from './ui/Modal';
import { Input } from './ui/Input';
import { Button } from './ui/Button';
import { Dropdown } from './ui/Dropdown';
import { Therapist, Package } from '../types/firestoreTypes';
import { THERAPIST_STATUS, TherapistStatus } from '../types/constants';
import { useTranslation } from 'react-i18next';
import { useToast } from '../hooks/useToast';

interface EditTherapistModalProps {
  isOpen: boolean;
  onClose: () => void;
  therapist: Therapist | null; // Should always be a Therapist when isOpen is true
  onSave: (data: { status: TherapistStatus; currentPackageId: string }) => void;
  availablePackages: Package[];
  isLoadingPackages?: boolean;
}

const STATUS_OPTIONS = Object.values(THERAPIST_STATUS).map(status => ({
  value: status,
  label: status,
}));

const EditTherapistModal: React.FC<EditTherapistModalProps> = ({
  isOpen,
  onClose,
  therapist,
  onSave,
  availablePackages,
  isLoadingPackages = false
}) => {
  const { t, i18n } = useTranslation();
  const { addToast } = useToast();
  const [formData, setFormData] = useState({
    status: THERAPIST_STATUS.ACTIVE as TherapistStatus,
    currentPackageId: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize form data when modal opens with a therapist
  useEffect(() => {
    if (isOpen && therapist) {
      setFormData({
        status: therapist.status,
        currentPackageId: therapist.currentPackageId || '',
      });
    }
  }, [isOpen, therapist]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.status) {
      newErrors.status = t('editTherapistModal.errors.statusRequired');
    }
    if (!formData.currentPackageId) {
      newErrors.currentPackageId = t('editTherapistModal.errors.packageRequired');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) { // Basic validation for required fields
      return;
    }

    const trialPackage = availablePackages.find(p => p.id === 'trial');
    let trialConsistencyError = false;

    // Trial consistency checks (only if trialPackage is defined in availablePackages)
    if (trialPackage) {
      const isStatusTrial = formData.status === THERAPIST_STATUS.TRIAL;
      const isPackageTrial = formData.currentPackageId === trialPackage.id;

      if ((isStatusTrial && !isPackageTrial) || (isPackageTrial && !isStatusTrial)) {
        trialConsistencyError = true;
      }
    }

    if (trialConsistencyError) {
      addToast({
        type: 'error',
        message: t('editTherapistModal.errors.trialConsistencyError'),
      });
      return; // Prevent saving
    }

    setIsSubmitting(true);
    try {
      await onSave({
        status: formData.status,
        currentPackageId: formData.currentPackageId,
      });
      onClose(); // Close modal on successful save (via AdminPage's feedback)
    } catch (error) {
      console.error('Error in onSave callback:', error);
      addToast({
        type: 'error',
        message: t('editTherapistModal.errors.saveFailed'),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const packageOptions = isLoadingPackages
    ? [{ value: '', label: t('editTherapistModal.loadingPackages') }]
    : [
        { value: '', label: t('editTherapistModal.selectPackage') },
        ...availablePackages.map(pkg => {
          const lang = i18n.language.split('-')[0];
          const name = (pkg as any)[`name_${lang}`] || pkg.name_en || pkg.name || pkg.id;
          return { value: pkg.id, label: name };
        })
      ];

  const modalFooter = (
    <>
      <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
        {t('common.cancel')}
      </Button>
      <Button
        variant="primary"
        onClick={handleSubmit}
        disabled={isSubmitting}
        isLoading={isSubmitting}
      >
        {t('common.saveChanges')}
      </Button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('editTherapistModal.title')}
      size="md"
      footer={modalFooter}
    >
      <div className="space-y-6">
        <Input
          label={t('editTherapistModal.name')}
          value={therapist?.displayName || ''}
          readOnly
          className="bg-[#F9FAFB]"
        />

        <Input
          label={t('editTherapistModal.email')}
          type="email"
          value={therapist?.email || ''}
          readOnly
          className="bg-[#F9FAFB]"
        />

        <Dropdown
          label={t('editTherapistModal.status')}
          options={STATUS_OPTIONS}
          value={formData.status}
          onChange={(value) => setFormData({ ...formData, status: value as TherapistStatus })}
          error={errors.status}
          disabled={isSubmitting}
        />

        <Dropdown
          label={t('editTherapistModal.package')}
          options={packageOptions}
          value={formData.currentPackageId}
          onChange={(value) => setFormData({ ...formData, currentPackageId: value })}
          error={errors.currentPackageId}
          disabled={isSubmitting}
        />
      </div>
    </Modal>
  );
};

export default EditTherapistModal;